/**
 * Как выглядит игрок в статистике: знак, карточка пьедестала и строка списка.
 *
 * Вынесено из страницы, потому что рисуется в двух местах сразу и потому что
 * страница и без того длинная. Здесь только вид — что показывать и в каком
 * порядке, решает `lib/stats.ts`.
 */

import type { ReactNode } from 'react';
import { tierLabel, toTier, type Tier } from '@ak/shared';

import { formatShortDate } from '../lib/format.ts';
import { useLinkHandler } from '../lib/router.ts';
import { formatKd, playerName, winRate, type CaptureActivity, type StatsPlayer } from '../lib/stats.ts';
import { Badge } from './ui.tsx';
import { TierPicker } from './tier-picker.tsx';

const number = (value: number): string => Math.round(value).toLocaleString('ru');

/** Ключ ника вместо статика (`n_имя_фамилия`) — показывать его незачем, ник и так виден. */
const isNickKey = (staticId: string): boolean => staticId.startsWith('n_');

/**
 * Ссылка на страницу игрока со статистикой — на том сервере, что выбран
 * на странице (сервер страница игрока берёт из того же сохранённого выбора).
 */
function playerHref(player: StatsPlayer): string {
  return `/stats/statics/${encodeURIComponent(player.staticId)}`;
}

/** Цвет знака игрока — по тиру, как везде в админке. */
function tierColor(tier: number | null): string {
  return tier === null || tier === 0 ? 'var(--tier-none)' : `var(--tier-${String(tier)})`;
}

/**
 * Аватарка человека, а без неё — знак с первой буквой ника.
 *
 * Аватарка приходит из Discord и попадает в анкету при сверке тиров — она
 * проходит по всем анкетам при каждом запуске бота. Пока её нет (анкеты не
 * завели или сверка ещё не проходила), рисуется буква на цвете тира: в
 * составах наборов человека и так читают по нику.
 *
 * Рамка цвета тира остаётся в обоих случаях — по ней тир видно и на аватарке.
 */
function PlayerMark({
  player,
  size = 40,
  eager = false,
}: {
  player: StatsPlayer;
  size?: number;
  /** Грузить сразу: так помечены те, кто виден без прокрутки. */
  eager?: boolean;
}): ReactNode {
  const color = tierColor(player.tier);

  /*
   * Аватарка кладётся поверх знака, а не вместо него. Так у клетки всегда
   * своя ширина и высота: не загрузись картинка — Discord прилёг, адрес
   * устарел, человек сменил аватарку — на её месте останется буква, а не
   * схлопнувшийся в пару пикселей блок. На такое уже наступали при проверке.
   */
  return (
    <span
      aria-hidden="true"
      className="relative grid shrink-0 place-items-center overflow-hidden rounded-xl font-semibold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        color,
        background: `color-mix(in oklab, ${color} 18%, transparent)`,
        border: `1px solid color-mix(in oklab, ${color} 45%, transparent)`,
        fontFamily: 'var(--font-rounded)',
      }}
    >
      {playerName(player).slice(0, 1).toUpperCase()}
      {player.avatarUrl !== null && (
        <img
          src={player.avatarUrl}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          className="absolute inset-0 size-full object-cover"
        />
      )}
    </span>
  );
}

function TierMark({ tier }: { tier: number | null }): ReactNode {
  // Через `toTier`: после проверки на ноль компилятор сузит тип до
  // назначенного тира, и подпись берётся из общего словаря без приведения.
  const assigned = tier === null ? 0 : toTier(tier);
  if (assigned === 0) {
    return null;
  }

  const color = tierColor(assigned);

  return (
    <span
      className="rounded-md px-1.5 py-0.5 text-[0.65rem] font-semibold"
      style={{ color, background: `color-mix(in oklab, ${color} 15%, transparent)` }}
    >
      {tierLabel(assigned)}
    </span>
  );
}

/** Доля побед строкой. Прочерк — итогов у человека ещё нет. */
function WinRate({ player }: { player: StatsPlayer }): ReactNode {
  const rate = winRate(player);
  if (rate === null) {
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  }

  return <span style={{ color: rate >= 0.5 ? 'var(--positive)' : 'var(--danger)' }}>{Math.round(rate * 100)}%</span>;
}

/** Число с подписью — из них собраны и карточки, и пьедестал. */
export function Metric({
  label,
  value,
  big = false,
  align = 'left',
}: {
  label: string;
  value: ReactNode;
  big?: boolean;
  align?: 'left' | 'right';
}): ReactNode {
  return (
    <div className={align === 'right' ? 'text-right' : undefined}>
      <div
        className={big ? 'text-2xl font-bold tabular-nums' : 'text-sm font-semibold tabular-nums'}
        style={big ? { fontFamily: 'var(--font-rounded)' } : undefined}
      >
        {value}
      </div>
      <div className="truncate text-[0.65rem] tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
    </div>
  );
}

const MEDALS = ['Первое место', 'Второе место', 'Третье место'];

/**
 * Пьедестал: первая тройка по выбранному порядку.
 *
 * Показывается, только пока отбор пуст. Тройка внутри отфильтрованного
 * подмножества врала бы: «первый среди тира 3 с пятью каптами» — это не топ
 * семьи, а обрывок выборки.
 */
export function Podium({ players, timezone }: { players: StatsPlayer[]; timezone: string }): ReactNode {
  const onLinkClick = useLinkHandler();

  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-3">
      {players.map((player, index) => {
        const place = index + 1;
        const medal = `var(--medal-${String(place)})`;
        const soft = `var(--medal-${String(place)}-soft)`;

        return (
          <a
            key={player.staticId}
            href={playerHref(player)}
            onClick={onLinkClick}
            className="relative overflow-hidden rounded-2xl p-4 transition-transform hover:-translate-y-0.5"
            style={{
              background: 'var(--surface-panel)',
              border: `1px solid color-mix(in oklab, ${medal} 40%, transparent)`,
              boxShadow: `0 12px 32px -14px color-mix(in oklab, ${medal} 70%, transparent)`,
              // Второе и третье место стоят чуть ниже первого — пьедестал
              // читается порядком, а не только цветом.
              marginTop: place === 1 ? 0 : place === 2 ? '0.5rem' : '0.75rem',
            }}
            aria-label={`${MEDALS[index] ?? ''}: ${playerName(player)}`}
          >
            {/* Свечение медали в углу карточки. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -top-10 -right-10 size-28 rounded-full blur-2xl"
              style={{ background: soft }}
            />

            <div className="relative flex items-center gap-3">
              {/* Пьедестал виден сразу — его аватарки не откладываем. */}
              <PlayerMark player={player} size={44} eager />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{playerName(player)}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {!isNickKey(player.staticId) && <span>{player.staticId}</span>}
                  <TierMark tier={player.tier} />
                </div>
              </div>
              <span
                className="grid size-8 shrink-0 place-items-center rounded-full text-sm font-bold"
                style={{ color: medal, background: soft, fontFamily: 'var(--font-rounded)' }}
              >
                {place}
              </span>
            </div>

            {/* Киллы первыми: по ним считается место, и глаз должен падать
                на то самое число, которое поставило человека сюда. */}
            <div className="relative mt-4 grid grid-cols-3 gap-2">
              <Metric label="киллы" value={number(player.kills)} big />
              <Metric label="k/d" value={formatKd(player)} big />
              <Metric label="мп" value={number(player.captures)} big />
            </div>

            <div
              className="relative mt-3 flex items-center justify-between text-xs"
              style={{ color: 'var(--text-muted)' }}
            >
              <span>урон {number(player.damage)}</span>
              <WinRate player={player} />
            </div>

            <div className="relative mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
              последний капт {formatShortDate(player.lastCaptureAt, timezone)}
            </div>
          </a>
        );
      })}
    </div>
  );
}

/** Строка-карточка игрока: та же карточка, только плотнее. */
export function PlayerRow({
  player,
  place,
  timezone,
  activity,
  canChangeTier = false,
}: {
  player: StatsPlayer;
  /** Номер в текущем порядке. `null` — идёт отбор, и нумеровать нечего. */
  place: number | null;
  timezone: string;
  activity: CaptureActivity;
  /** Пускать ли к смене тира. По умолчанию нет: страницу видит вся семья. */
  canChangeTier?: boolean;
}): ReactNode {
  const onLinkClick = useLinkHandler();

  return (
    <a
      href={playerHref(player)}
      onClick={onLinkClick}
      className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl px-3 py-3 transition-colors hover:bg-[var(--surface-hover)]"
      style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)' }}
    >
      {place !== null && (
        <span
          className="w-6 shrink-0 text-center text-sm font-semibold tabular-nums"
          style={{ color: 'var(--text-muted)' }}
        >
          {place}
        </span>
      )}

      <PlayerMark player={player} />

      <div className="min-w-[9rem] flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{playerName(player)}</span>
          {/* Тир правится только у того, кто заведён в Discord: без анкеты
              менять роль некому — игровая служба про Discord не знает. */}
          {canChangeTier && player.userId !== null ? (
            <TierPicker userId={player.userId} tier={(player.tier ?? 0) as Tier} />
          ) : (
            <TierMark tier={player.tier} />
          )}
          {player.userId === null && <Badge tone="warning">без анкеты</Badge>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs" style={{ color: 'var(--text-muted)' }}>
          {!isNickKey(player.staticId) && <span>{player.staticId} ·</span>}
          {player.discordName !== null && <span>{player.discordName} ·</span>}
          <span>последний бой {formatShortDate(player.lastCaptureAt, timezone)}</span>
          {activity.month > 0 && (
            <span>
              · за месяц {player.capturesMonth} из {activity.month}
            </span>
          )}
        </div>
      </div>

      {/*
        Ширина колонок задана жёстко, а числа прижаты вправо. Без этого блок
        цифр начинался там, где кончалось имя, и у длинных ников уезжал: в
        соседних строках одни и те же колонки стояли на разной высоте по
        горизонтали, и глазами столбик было не прочитать.

        На узком экране блок переносится под имя целиком и делится на три
        колонки — так он остаётся столбиком, а не лесенкой.
      */}
      <div className="grid w-full grid-cols-3 gap-x-3 gap-y-2 sm:w-[30rem] sm:shrink-0 sm:grid-cols-6 sm:gap-y-0">
        <Metric align="right" label="мп" value={number(player.captures)} />
        <Metric align="right" label="киллы" value={number(player.kills)} />
        <Metric
          align="right"
          label="смерти"
          value={player.deaths === null || player.deaths === undefined ? '—' : number(player.deaths)}
        />
        <Metric align="right" label="k/d" value={formatKd(player)} />
        <Metric align="right" label="урон" value={number(player.damage)} />
        <Metric align="right" label="винрейт" value={<WinRate player={player} />} />
      </div>
    </a>
  );
}
